import React, {createContext, useEffect, useContext, useRef, useState} from "react";
import {retrieveTempUsername, retrieveUser} from "./clients/UserClient";
import {clearStoredSessionId, getStoredSessionId} from "./utils/sessionStore";
import {LOGIN_EXPIRED_EVENT} from "./utils/loginExpiry";
import socket from "./utils/clientSocket";

const UserContext = createContext({loaded: false})

export const useUser = () => useContext(UserContext);

export const UserProvider = ({children}) => {
    //we also have temp users who are not logged in - so this object is {username: "rival-giraffe", isLoggedIn: false}
    //`loaded` tells components whether we have heard back from the server yet
    const [user, setUser] = useState({loaded: false});
    const [sessionId, setSessionId] = useState(getStoredSessionId())

    const userRef = useRef(user);
    userRef.current = user;

    // The login ran out while the page was open: continue as a guest everywhere at once. The live connection is
    // restarted without the old session, so chat, games and "online" status also stop using the account.
    useEffect(() => {
        const handleExpired = () => {
            if (!userRef.current.isLoggedIn) return;
            clearStoredSessionId();
            socket.auth = {};
            socket.sessionId = undefined;
            socket.disconnect().connect();
            setUser({isLoggedIn: false, loaded: true, loginExpired: true});
        };
        window.addEventListener(LOGIN_EXPIRED_EVENT, handleExpired);
        return () => window.removeEventListener(LOGIN_EXPIRED_EVENT, handleExpired);
    }, []);

    useEffect(() => {
        const handleSessionUpdate = (event) => setSessionId(event.detail.sessionId);
        window.addEventListener('session-id-updated', handleSessionUpdate)
        return () => window.removeEventListener('session-id-updated', handleSessionUpdate)
    }, []);

    useEffect(() => {
        let cancelled = false;
        //There is a theoretical race condition here, if the session id is updated while the below promises are being
        //resolved. It would only cause a wrong temporary username until the next refresh, so we accept it.
        retrieveUser()
            .then((data) => !cancelled && setUser({username: data.username, isLoggedIn: true, loaded: true}))
            .catch(() => {
                if (cancelled) return;
                if (sessionId) {
                    retrieveTempUsername(sessionId)
                        .then((data) => !cancelled && setUser({username: data.username, isLoggedIn: false, loaded: true}))
                        .catch((e) => {
                            console.error("Something went wrong when retrieving temp username: ", e);
                            if (!cancelled) setUser({isLoggedIn: false, loaded: true});
                        })
                } else {
                    setUser({isLoggedIn: false, loaded: true});
                }
            })
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return <UserContext.Provider value={user}>{children}</UserContext.Provider>
}
